import React, { useEffect, useRef, useState } from "react";
import { Mic, Play, Pause, Lock } from "lucide-react";
import type { ChatVoice } from "../../services/rtkApi/agentApi";
import { useAppSelector } from "../../store/hooks";
import { voiceAudioSrc } from "../../config/voiceAudio";
import { AudioScrubBar } from "./AudioScrubBar";

interface VoiceMessageBodyProps {
  voice: ChatVoice;
  /** how many times THIS user has already played this clip (server truth) */
  plays: number;
  /** allowed plays before the audio locks (hear once + one replay = 2) */
  maxPlays: number;
  /** called when a fresh listen starts, so the play is recorded server-side */
  onPlay: () => void;
}

function fmtClock(seconds: number): string {
  const s = isFinite(seconds) && seconds > 0 ? seconds : 0;
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}

// renders a voice note inside a chat bubble. the audio is heard once (plus one replay); after the
// first listen it reveals the italic fun summary. once the replay is used the audio locks.
export function VoiceMessageBody({ voice, plays, maxPlays, onPlay }: VoiceMessageBodyProps) {
  const token = useAppSelector((s) => s.auth.token);
  const audioRef = useRef<HTMLAudioElement>(null);
  const [localPlays, setLocalPlays] = useState(plays);
  const [playing, setPlaying] = useState(false);
  // a listen is in progress (playing or paused mid-clip) — lets pause/resume without burning a play
  const [active, setActive] = useState(false);
  const [current, setCurrent] = useState(0);
  // seed the total from the recorder's count; refine once <audio> metadata loads
  const [duration, setDuration] = useState((voice.durationMs ?? 0) / 1000);

  useEffect(() => {
    setLocalPlays((p) => Math.max(p, plays));
  }, [plays]);

  const revealed = localPlays > 0;
  const canStart = localPlays < maxPlays;
  const replaysLeft = Math.max(0, maxPlays - localPlays);
  const playable = playing || active || canStart;
  const progress = duration > 0 ? current / duration : 0;

  const handleToggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      return;
    }
    if (active) {
      // resume the same listen — no new play consumed
      el.play().catch(() => undefined);
      return;
    }
    if (!canStart) return;
    // start a brand-new listen
    setLocalPlays((p) => p + 1);
    setActive(true);
    onPlay();
    el.currentTime = 0;
    setCurrent(0);
    el.play().catch(() => undefined);
  };

  // scrub within the current listen only (no skipping before a listen has started)
  const seek = (ratio: number) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    el.currentTime = ratio * duration;
    setCurrent(el.currentTime);
  };

  return (
    <div className="flex flex-col gap-1.5 min-w-[170px]">
      <audio
        ref={audioRef}
        src={voiceAudioSrc(voice.audioUrl, token)}
        preload="none"
        className="hidden"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setActive(false);
        }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (isFinite(d) && d > 0) setDuration(d);
        }}
      />
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={handleToggle}
          disabled={!playable}
          className={`flex items-center justify-center w-9 h-9 rounded-full border-2 border-ink shrink-0 transition-colors ${
            playable
              ? "bg-accent/30 hover:bg-accent/50"
              : "bg-gray-200 opacity-60 cursor-not-allowed"
          }`}
          aria-label={playing ? "pause voice message" : canStart ? "play voice message" : "voice message locked"}
        >
          {playing ? (
            <Pause className="w-4 h-4" />
          ) : canStart || active ? (
            <Play className="w-4 h-4" />
          ) : (
            <Lock className="w-3.5 h-3.5" />
          )}
        </button>
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] md:text-xs text-gray-500 flex items-center gap-1 min-w-0">
              <Mic className="w-3 h-3 shrink-0" />
              <span className="truncate">{revealed ? "voice message" : "tap to listen"}</span>
            </span>
            <span className="text-[9px] md:text-[10px] text-gray-400 shrink-0 tabular-nums">
              {fmtClock(current)}/{fmtClock(duration)}
            </span>
          </div>
          <AudioScrubBar progress={progress} muted={!playable} onSeek={active ? seek : undefined} />
        </div>
      </div>
      {revealed && voice.funSummary && (
        <span className="italic text-xs md:text-sm break-words leading-snug">{voice.funSummary}</span>
      )}
      {revealed && (
        <span className="text-[9px] md:text-[10px] text-gray-400 flex items-center gap-1">
          {replaysLeft > 0 ? (
            `${replaysLeft} replay${replaysLeft === 1 ? "" : "s"} left`
          ) : (
            <>
              <Lock className="w-2.5 h-2.5" /> no replays left
            </>
          )}
        </span>
      )}
    </div>
  );
}
